import React, { type FC, useState, useEffect, useMemo } from "react";
import { type FormikErrors, useFormik } from 'formik';
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from "@apollo/client";
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import InfoIcon from '@mui/icons-material/Info';
import { useSnackbar } from "notistack";

import { Button, DateInput, FileUpload, Select, TextBox } from 'components/Shared/Inputs';
import { LICENSE_REF_DATA, type LicenseRefDataData } from "./queries";
import { confirm } from 'components/Shared/ConfirmDialog';
import { DATE_FORMATS } from "hooks/useTimezoneUtils";
import { ApiError } from "api/error/ApiError";
import { useAddCredential, useReplaceCredential, useUpdateCredential } from "api/queries/credentialsQuery";
import { type CredentialResponse, type License, type ViewMode } from '../../../interfaces';

const mapApiResponseToLicense = (response: CredentialResponse, refData?: LicenseRefDataData): License => {
    const { license } = response;
    return ({
        ...(license),
        licenseRegistrationAssociation: refData?.licenseRegistrationAssociations?.find(assoc => assoc?.value === license?.licenseType?.toString())?.title ?? "",
        provinceString: refData?.provinces?.find(prov => prov?.value === license.province?.toString())?.title ?? "",
        licenseTypeDescr: null,
        // @ts-ignore
        __typename: "License"
    });
};

interface EditLicenseFormProps {
    providerId: number;
    open: boolean;
    setModalOpen: (modalOpen: boolean) => void;
    licenses?: License[];
    license?: License;
    cachePendingUpdate: boolean;
    viewMode?: ViewMode;
    refreshAttachments?: () => void;
    onLicensesUpdated: (licenses: License[]) => void;
}

interface EditLicenseFormValues {
    providerLicenseId: number | null;
    registrationNo: string;
    countryCode: string | null;
    province: number | null;
    licenseType: number | null;
    expiryDate: string;
}

export const EditLicenseModal: FC<EditLicenseFormProps> = (props) => {
    const { providerId, setModalOpen, licenses, license, cachePendingUpdate, open, refreshAttachments, viewMode = 'upload-only', onLicensesUpdated } = props;

    const { t } = useTranslation('providerprofile', { useSuspense: false });
    const { t: t_common } = useTranslation('common', { useSuspense: false });

    const { enqueueSnackbar } = useSnackbar();

    const [requestPending, setRequestPending] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File|null>(null);
    const [fileError, setFileError] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [confirmedValidCopy, setConfirmedValidCopy] = useState(false);
    
    const [getRefData, refDataQuery] = useLazyQuery(LICENSE_REF_DATA);

    const updateLicenses = (data?: CredentialResponse) => {
        const newLicense = mapApiResponseToLicense(data!, refDataQuery.data);
        let currentLicenseIndex = licenses?.findIndex(lic => [license?.providerLicenseId, newLicense?.providerLicenseId].includes(lic.providerLicenseId)) ?? licenses?.length ?? 0;
        currentLicenseIndex = currentLicenseIndex === -1 ? (licenses?.length ?? 0) : currentLicenseIndex;
        const updatedLicenses = [...(licenses || [])];
        updatedLicenses.splice(currentLicenseIndex, 1, newLicense!);
        onLicensesUpdated(updatedLicenses);
    }

    const onError = (error: Error) => {
        if (error instanceof ApiError && error.isBusinessError) {
            setErrorCode(error.code);
        } else {
            setUploadError(`${t('licenses_registrations__doc_upload_failed')}: ${error.message}`);
            enqueueSnackbar(`${t('licenses_registrations__doc_upload_failed')}: ${error.message}`, { variant: "error" });
        }
        return error;
    };

    const showSuccessMessageAndRefresh = (message: string, data?: CredentialResponse) => {
        enqueueSnackbar(message, { variant: "success" });
        if (viewMode !== 'edit') {
            refreshAttachments?.()
        }
        updateLicenses(data);
        resetAndCloseModal();
    };
    const { isPending: addPending, mutateAsync: addLicense } = useAddCredential({
        onError,
        onSuccess: (data) => showSuccessMessageAndRefresh(t('licenses_registrations__new_license_added'), data)
    });

    const { isPending: updatePending, mutateAsync: updateLicense } = useUpdateCredential({
        onError,
        onSuccess: (data) => ({
            'upload-only': () => showSuccessMessageAndRefresh(t('licenses_registrations__doc_uploaded_successfully'), data),
            edit: () => showSuccessMessageAndRefresh(t('licenses_registrations__license_edited_successfully'), data),
        })[viewMode]()
    });

    const { isPending: renewPending, mutateAsync: renewLicense } = useReplaceCredential({
        onError,
        onSuccess: (data) => showSuccessMessageAndRefresh(t('licenses_registrations__license_renewed_successfully'), data)
    });

    const isPending = addPending || updatePending || renewPending;

    const apiMethods = {
        edit: updateLicense,
        new: addLicense,
        renew: renewLicense,
        'upload-only': updateLicense
    };

    useEffect(() => {
        if (!refDataQuery.called) {
            getRefData();
        }
    }, [viewMode, open]);

    const formik = useFormik<EditLicenseFormValues>({
        initialValues: {
            providerLicenseId: license?.providerLicenseId ?? null,
            registrationNo: license?.registrationNo ?? "",
            countryCode: license?.countryCode ?? null,
            province: license?.province ?? null,
            licenseType: license?.licenseType ?? null,
            expiryDate: viewMode === "renew" ? "" : license?.expiryDate ?? moment().format('YYYY-MM-DD')
        },
        enableReinitialize: true,
        validate: (values) => {
            const errors: FormikErrors<EditLicenseFormValues> = {};
            const isRenew = viewMode === 'renew';

            if (!values.registrationNo) {
                errors.registrationNo = "Registration number is required"
            }

            if (!values.expiryDate) {
                errors.expiryDate = "Expiry date is required"
            }

            if (values.expiryDate === "Invalid date") {
                errors.expiryDate = "Expiry date is invalid. Please enter a valid date"
            }

            if (!values.licenseType) {
                errors.licenseType = "License type is required"
            }

            if (!isRenew && !values.province) {
                errors.province = "Province is required"
            }

            return errors;
        },
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: doSubmit
    });

    async function doSubmit(values) {
        formik.validateForm();

        if (formik.isValid && (viewMode === 'edit' || !!selectedFile)) {
            setRequestPending(true);
            try {
                await (apiMethods[viewMode])({
                        providerId: providerId.toString(),
                        ...values,
                        file: !!selectedFile ? selectedFile : undefined,
                });
                setRequestPending(false);
            } catch (err) {
                setRequestPending(false);
                console.error(err);
            }
        }
    }

    const hasChanges = formik.dirty 
        && (formik.values.countryCode !== license?.countryCode
            || formik.values.licenseType !== license?.licenseType
            || formik.values.province !== license?.province
            || formik.values.registrationNo !== license?.registrationNo
            || formik.values.expiryDate !== license?.expiryDate);

    const formInvalid = viewMode !== 'upload-only' && (!formik.isValid || !hasChanges);
    const hasFileErrors = viewMode !== 'edit' && (fileError || !selectedFile || !!errorCode || !!uploadError);
    const checkboxUnchecked = (viewMode !== 'edit' && !confirmedValidCopy);
    const cancelDisabled = cachePendingUpdate || isPending;
    const submitDisabled = cancelDisabled || formInvalid || checkboxUnchecked || hasFileErrors;
    const checkboxDisabled = formInvalid || hasFileErrors;

    const handleUploadOnly = async () => {
        return doSubmit(formik.values);
    }
    
    const resetAndCloseModal = () => {
        formik.resetForm();
        setConfirmedValidCopy(false);
        setSelectedFile(null);
        setUploadError('');
        setErrorCode('');
        setModalOpen(false);
    }

    const handleCancelChanges = async () => {
        if (viewMode !== 'upload-only' && hasChanges) {
            const dialogProps = {
                title: t('common__discard_changes_dialog_title'),
                message: t('common__discard_changes_dialog_text')
            }
            if (await confirm(dialogProps)) {
                return resetAndCloseModal();
            }
        } else {
            resetAndCloseModal();
        }
    }

    const associations = useMemo(() =>
        refDataQuery.data?.licenseRegistrationAssociations?.map(e => ({title: e.title, value: Number(e.value)}))
    , [refDataQuery.data?.licenseRegistrationAssociations]);
    const provinces = useMemo(() =>
        refDataQuery.data?.provinces?.map(e => ({title: e.title, value: Number(e.value)}))
    , [refDataQuery.data?.provinces]);

    
    const handleExpiryDateChange = (date) => {
        date &&
        formik.setFieldValue('expiryDate', date.format('YYYY-MM-DD'));   
    }

    const handleConfirm = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmedValidCopy(e?.target?.checked);
    }
    
    const handleFileSelect = (file: File | null) => {
        setUploadError('');
        setErrorCode('');
        setFileError(false);
        setSelectedFile(file);
    }

    const dialogTitlesByViewMode = {
        new: t('licenses_registrations__add_new'),
        renew: t('licenses_registrations__renew'),
        'upload-only': t('licenses_registrations__upload_doc'),
        edit: t('licenses_registrations__edit'),
    }

    return (
        <Modal open={open} className="dialog_wrapper license_modal" role="dialog" aria-modal aria-labelledby="license_dialog_title" aria-describedby="license_dialog_content">
            <div className='dialog_surface'>
                <Container maxWidth="md">  
                    <div className="dialog_title">
                        <h2 id="license_dialog_title" style={{ fontWeight: '400' }}>
                            {dialogTitlesByViewMode[viewMode]}
                        </h2>
                        {!['upload-only', 'edit'].includes(viewMode) && <p style={{ marginBottom: '24px' }}>{viewMode === 'new' ? t('licenses_registrations__instructions') : t('licenses_registrations__instructions_renew')}</p> }
                        {viewMode === 'renew' && (license?.attachments?.length ?? 0) > 0 && (
                            <Alert style={{ marginTop: "16px", borderLeft: "6px solid var(--color_primary)" }} variant="standard" color="info" icon={<InfoIcon color="primary" />}>
                                {t('licenses_registrations__renew_warning')}
                            </Alert>
                        )}
                    </div>
                    <div id="license_dialog_content">
                        <Grid className="credentialing_sub_section_form" container component='form' onSubmit={formik.handleSubmit} spacing={3}>
                            {viewMode !== 'upload-only' &&
                                <>
                                    {viewMode === 'new' && <Grid item xs={12} xl={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                label={t('licenses_registrations__association_label')}
                                                name="licenseType"
                                                value={formik.values.licenseType ?? ''} 
                                                onChange={formik.handleChange}
                                                fullWidth   
                                            >
                                                {
                                                    associations?.map(licenseRegistrationAssociation => (
                                                        <MenuItem value={licenseRegistrationAssociation.value} key={licenseRegistrationAssociation.value}>
                                                            {licenseRegistrationAssociation.title}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>}
                                    {viewMode !== 'edit' && <Grid item xs={12} xl={6}>
                                        <FormControl fullWidth>
                                            <DateInput
                                                label={t('licenses_registrations__expiry_date_label')}
                                                name="expiryDate"
                                                value={viewMode === 'renew' ? undefined : moment(formik.values.expiryDate, DATE_FORMATS.ISO)}
                                                onChange={handleExpiryDateChange}
                                                slotProps={{ textField: { style: { width: '100%' } } }}
                                                error={Boolean(formik.errors.expiryDate)}
                                            />
                                        </FormControl>
                                    </Grid>}
                                    {viewMode !== 'renew' && <Grid item xs={12} xl={6}>
                                        <FormControl fullWidth>
                                            <TextBox 
                                                value={formik.values.registrationNo} 
                                                label={t('licenses_registrations__registration_number_label')}
                                                name="registrationNo" 
                                                onChange={formik.handleChange}
                                                error={Boolean(formik.errors.registrationNo)}
                                                fullWidth
                                            />
                                        </FormControl>
                                    </Grid>}
                                    {viewMode !== 'renew' && <Grid item xs={12} xl={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                value={formik.values.province ?? ''} 
                                                label={t('licenses_registrations__province_label')}
                                                name="province" 
                                                onChange={formik.handleChange}
                                                error={Boolean(formik.errors.province)}
                                                type="number"
                                                fullWidth
                                            >
                                                {
                                                    provinces?.map(province => (
                                                        <MenuItem value={province.value} key={province.value}>
                                                            {province.title}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>}
                                </>
                            }
                            {viewMode !== 'edit' && (
                                <>
                                    <Grid item xs={12} xl={12}>
                                        <FileUpload onFileSelect={handleFileSelect} onFileError={setFileError} uploadError={uploadError} uploadErrorCode={errorCode} />
                                    </Grid>
                                    <Grid item xs={12} xl={12}>
                                        <FormControlLabel disabled={checkboxDisabled} label={t('liability_insurance__comfirm_valid_doc')} control={
                                            <Checkbox
                                                checked={confirmedValidCopy}
                                                onChange={handleConfirm}
                                                name="confirmedValidCopy"
                                                color="primary"
                                                disabled={checkboxDisabled}
                                            />
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </div>
                    <div className="dialog_actions_wrapper">
                        <Button
                            busy={requestPending}
                            busyText={t_common('saving')}
                            type="submit"
                            disabled={submitDisabled}
                            onClick={viewMode==='upload-only' ? handleUploadOnly : formik.submitForm}
                        >
                            {t_common('save')}
                        </Button>
                        <Button type="button" variant="text" onClick={handleCancelChanges}>
                            {t_common('cancel')}
                        </Button>
                    </div>
                </Container>
            </div>
        </Modal>
    );
}